/**
 * The sidebar of an app area: iManage, Contract Intelligence and Probe are
 * whole products, so each brings its own navigation, the way Home and
 * Governance do: the app's own menu (APP_AREA_NAVS). The app's name is the
 * top bar's last breadcrumb (AppBreadcrumb); below md, where there is no top
 * bar, the sidebar names it and leads back to the launcher instead.
 *
 * The launcher, the directory and apps without an area have no sidebar:
 * AppSidebarLayout leaves it out there and the page is the whole card.
 *
 * @module components/apps/AppsSidebar
 */
import { Link } from "@tanstack/react-router";
import { ChevronLeftIcon } from "lucide-react";
import type { ComponentType } from "react";

import { SidebarContent, SidebarGroup, useSidebar } from "../ui/sidebar";
import { useAppAreaHere } from "./AppBreadcrumb";
import { AppIcon } from "./AppParts";
import type { AppAreaId } from "./appsPaths";
import { ContractIntelligenceSidebarNav } from "./contractIntelligence/ContractIntelligenceSidebarNav";
import { IManageSidebarNav } from "./imanage/IManageSidebarNav";
import { ProbeSidebar } from "./probe/sidebar/ProbeSidebar";

/** What each area's menu receives. */
export interface AppAreaNavProps {
  readonly orgSegment: string;
  readonly organizationId: string;
  readonly pathname: string;
  /** Closes the sidebar sheet on phones after a pick. */
  readonly onNavigate: () => void;
}

export const APP_AREA_NAVS: Readonly<Record<AppAreaId, ComponentType<AppAreaNavProps>>> = {
  imanage: IManageSidebarNav,
  "contract-intelligence": ContractIntelligenceSidebarNav,
  probe: ProbeSidebar,
};

export function AppsSidebar() {
  const here = useAppAreaHere();
  const { isMobile, setOpenMobile } = useSidebar();
  const onNavigate = () => {
    if (isMobile) setOpenMobile(false);
  };

  if (here === null) return <SidebarContent data-apps-sidebar="" />;
  const { area, orgId, orgSegment, pathname, entry, name } = here;
  const Nav = APP_AREA_NAVS[area];

  return (
    <SidebarContent
      className="min-h-full overflow-x-hidden"
      data-apps-sidebar=""
      data-app-area={area}
    >
      <SidebarGroup className="flex-1 gap-3 p-[var(--sidebar-content-inset)] pt-2.5">
        <div className="flex flex-col gap-1.5 px-1 md:hidden">
          <Link
            to="/$org/apps"
            params={{ org: orgSegment }}
            onClick={onNavigate}
            data-app-area-back=""
            className="-ms-1 inline-flex w-fit items-center gap-0.5 rounded-md px-1 py-0.5 text-xs text-sidebar-muted-foreground outline-hidden transition-colors hover:text-sidebar-foreground focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronLeftIcon aria-hidden className="size-3.5" />
            Apps
          </Link>
          <div className="flex h-8 min-w-0 items-center gap-2" data-app-area-title="">
            {entry ? <AppIcon entry={entry} className="size-6 rounded-md" /> : null}
            <span className="truncate text-sm font-semibold text-sidebar-foreground">{name}</span>
          </div>
        </div>
        <Nav
          orgSegment={orgSegment}
          organizationId={orgId}
          pathname={pathname}
          onNavigate={onNavigate}
        />
      </SidebarGroup>
    </SidebarContent>
  );
}
